import PagedResourceSelect from '@app/components/PagedResourceSelect';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OrgModuleLayout from '@app/pages/org/OrgModuleLayout';
import OrgModulePlaceholder from '@app/pages/org/OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useApiQuery } from '@app/hooks/useApiQuery';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import Drawer from '@app/components/Drawer';
import { formatDateTime } from '@app/utils/format';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import { ShieldIcon, ChevronLeftIcon, ChevronRightIcon, TerminalIcon } from '@app/components/icons';
import { useDevicesApi, useSitesApi } from '@app/hooks/useApis';

// Interface matching DeviceCVEExposure contract
export interface DeviceCVEExposure {
  id?: string;
  orgId: string;
  deviceId: string;
  deviceName?: string;
  siteId?: string;
  siteName?: string;
  cveId: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  cvssScore?: number;
  status: 'EXPOSED' | 'NOT_EXPOSED';
  justification: string;
  causativeConfig?: string;
  remediationType?: 'UPGRADE' | 'FIX_CONFIG';
  patchUpgrade?: string;
  remediationCommands?: string[];
  impactOfFix?: string;
  snapshotId?: string;
  assessedAt?: string;
}

// Interface matching CVEGlobalSummary contract
export interface CVEGlobalSummary {
  totalCves: number;
  activeExposures: number;
  auditedSafe: number;
  exposureRatio: number;
  criticalExposures: number;
  highExposures: number;
  mediumExposures: number;
  lowExposures: number;
}

const PRESET_TIMELINE = [
  { label: 'Real-Time', offsetMs: 0 },
  { label: '1 Hr Ago', offsetMs: 60 * 60 * 1000 },
  { label: '6 Hrs Ago', offsetMs: 6 * 60 * 60 * 1000 },
  { label: '12 Hrs Ago', offsetMs: 12 * 60 * 60 * 1000 },
  { label: '24 Hrs Ago', offsetMs: 24 * 60 * 60 * 1000 },
  { label: '3 Days Ago', offsetMs: 3 * 24 * 60 * 60 * 1000 },
  { label: '7 Days Ago', offsetMs: 7 * 24 * 60 * 60 * 1000 }
];

const CVEGlobalSummaryPage = () => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource } = useAuthFetch();

  const [sliderIndex, setSliderIndex] = useState(0);
  const [customTimestamp, setCustomTimestamp] = useState<string>('');
  const [refreshVersion, setRefreshVersion] = useState(0);

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedSiteId, setSelectedSiteId] = useState<string>('ALL');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('ALL');

  // Table pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Active drawer assessment state
  const [selectedExposure, setSelectedExposure] = useState<DeviceCVEExposure | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Compute targeted ISO timestamp based on presets or custom input
  const resolvedTimestamp = useMemo(() => {
    if (customTimestamp) {
      const parsedDate = new Date(customTimestamp);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString();
      }
    }
    const preset = PRESET_TIMELINE[sliderIndex];
    if (!preset || preset.offsetMs === 0) {
      return null;
    }
    return new Date(Date.now() - preset.offsetMs).toISOString();
  }, [sliderIndex, customTimestamp]);

  // Fetch summary endpoint helper
  const summaryFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    const queryParts = [
      resolvedTimestamp ? `timestamp=${encodeURIComponent(resolvedTimestamp)}` : '',
      selectedSiteId !== 'ALL' ? `site_id=${encodeURIComponent(selectedSiteId)}` : '',
      selectedDeviceId !== 'ALL' ? `device_id=${encodeURIComponent(selectedDeviceId)}` : '',
    ].filter(Boolean);
    const queryStr = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return () => fetchOrgResource<CVEGlobalSummary>(basePath, orgId, `cves/summary${queryStr}`);
  }, [basePath, orgId, resolvedTimestamp, selectedSiteId, selectedDeviceId, refreshVersion]);

  // Fetch assessments list endpoint helper
  const assessmentsFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    const offset = (page - 1) * pageSize;
    const queryParts = [
      resolvedTimestamp ? `timestamp=${encodeURIComponent(resolvedTimestamp)}` : '',
      `limit=${pageSize}`,
      `offset=${offset}`,
      severityFilter !== 'ALL' ? `severity=${encodeURIComponent(severityFilter)}` : '',
      statusFilter !== 'ALL' ? `status=${encodeURIComponent(statusFilter)}` : '',
      selectedSiteId !== 'ALL' ? `site_id=${encodeURIComponent(selectedSiteId)}` : '',
      selectedDeviceId !== 'ALL' ? `device_id=${encodeURIComponent(selectedDeviceId)}` : '',
      searchQuery.trim() ? `search=${encodeURIComponent(searchQuery.trim())}` : '',
    ].filter(Boolean);
    const queryStr = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return () => fetchOrgResource<{ items: DeviceCVEExposure[]; meta: { limit: number; exactTotal?: number } }>(basePath, orgId, `cves/assessments${queryStr}`);
  }, [basePath, orgId, resolvedTimestamp, page, pageSize, severityFilter, statusFilter, selectedSiteId, selectedDeviceId, searchQuery, refreshVersion]);

  // Execute API Queries
  const { data: summaryData, loading: summaryLoading, error: summaryError } = useApiQuery<CVEGlobalSummary>(summaryFetcher, [summaryFetcher]);
  const { data: assessmentsData, loading: listLoading, error: listError } = useApiQuery<{ items: DeviceCVEExposure[]; meta: { limit: number; exactTotal?: number } }>(assessmentsFetcher, [assessmentsFetcher]);

  const loading = summaryLoading || listLoading;
  const apiError = summaryError || listError;

  // Sync pagination reset when changing filters or timeline
  useEffect(() => {
    setPage(1);
  }, [searchQuery, severityFilter, statusFilter, selectedSiteId, selectedDeviceId, resolvedTimestamp]);

  const handleRefresh = useCallback(() => {
    setRefreshVersion((prev) => prev + 1);
  }, []);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTimestamp(''); // Reset custom timestamp when using preset slider
    setSliderIndex(parseInt(event.target.value, 10));
  };

  const handleCustomTimestampChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTimestamp(event.target.value);
  };

  const handleClearCustomTimestamp = () => {
    setCustomTimestamp('');
    setSliderIndex(0);
  };

  // Filter local items based on search query
  const filteredExposures = useMemo(() => {
    return assessmentsData?.items ?? [];
  }, [assessmentsData]);

  // Pagination slice (Server-side slices for us)
  const pagedExposures = filteredExposures;

  // Columns for ListWorkspace
  const columns: Array<ListWorkspaceColumn<DeviceCVEExposure>> = [
    {
      key: 'cveId',
      header: 'CVE ID',
      width: '130px',
      render: (row) => (
        <span className="cve-pill-id">
          <strong>{row.cveId}</strong>
        </span>
      )
    },
    {
      key: 'deviceName',
      header: 'Device',
      render: (row) => (
        <div className="cve-cell-device">
          <strong className="cve-device-name">{row.deviceName || 'Unknown Device'}</strong>
          <span className="cve-device-sub">{row.siteName || 'Unknown Site'}</span>
        </div>
      )
    },
    {
      key: 'severity',
      header: 'Severity',
      width: '120px',
      align: 'center',
      render: (row) => {
        const sev = row.severity.toUpperCase();
        return (
          <span className={`cve-badge-severity sev-${sev.toLowerCase()}`}>
            {sev}
            {row.cvssScore !== undefined && <span className="cve-cvss-score">({row.cvssScore.toFixed(1)})</span>}
          </span>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      align: 'center',
      render: (row) => {
        const isExposed = row.status === 'EXPOSED';
        return (
          <span className={`cve-badge-status ${isExposed ? 'status-exposed' : 'status-safe'}`}>
            {isExposed ? 'EXPOSED' : 'AUDITED SAFE'}
          </span>
        );
      }
    },
    {
      key: 'remediation',
      header: 'Fix Type',
      width: '130px',
      render: (row) => {
        if (row.status !== 'EXPOSED') return <span className="cve-remediation-none">—</span>;
        const isUpgrade = row.remediationType === 'UPGRADE';
        return (
          <span className={`cve-remediation-type type-${isUpgrade ? 'upgrade' : 'config'}`}>
            {isUpgrade ? '🔀 Upgrade' : '🔧 Config Fix'}
          </span>
        );
      }
    },
    {
      key: 'assessedAt',
      header: 'Assessed',
      width: '160px',
      render: (row) => <span className="cve-timestamp">{formatDateTime(row.assessedAt)}</span>
    }
  ];

  const handleRowClick = (row: DeviceCVEExposure) => {
    setSelectedExposure(row);
    setCopiedIndex(null);
  };

  const handleCopyCommand = (command: string, index: number) => {
    navigator.clipboard.writeText(command);
    setCopiedIndex(index);
    setTimeout(() => {
      setCopiedIndex((prev) => (prev === index ? null : prev));
    }, 2000);
  };

  // KPI metadata computation
  const totalAssessments = summaryData ? (summaryData.activeExposures + summaryData.auditedSafe) : 0;
  const displayRatio = summaryData && totalAssessments > 0 ? ((summaryData.activeExposures / totalAssessments) * 100).toFixed(1) : '0.0';

  const filterBarContent = (
    <div className="cve-filter-grid">
      <label className="cve-filter-field">
        Search CVE or Device
        <input
          type="search"
          placeholder="e.g. CVE-2024-3807, Switch-A"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </label>
      <label className="cve-filter-field">
        Site
        <PagedResourceSelect resource="sites" value={selectedSiteId} allValue="ALL" allLabel="All sites" onChange={(id) => { setSelectedSiteId(id); setSelectedDeviceId("ALL"); }} />
      </label>
      <label className="cve-filter-field">
        Device
        <PagedResourceSelect resource="devices" value={selectedDeviceId} allValue="ALL" allLabel="All devices" siteId={selectedSiteId === "ALL" ? undefined : selectedSiteId} onChange={setSelectedDeviceId} />
      </label>
      <label className="cve-filter-field">
        Severity
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
          <option value="ALL">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
      </label>
      <label className="cve-filter-field">
        Status
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All Statuses</option>
          <option value="EXPOSED">Exposed</option>
          <option value="NOT_EXPOSED">Audited Safe</option>
        </select>
      </label>
    </div>
  );

  if (!orgId) {
    return (
      <OrgModuleLayout title="Vulnerability Assessments" description="Real-time exposure auditing, firmware scanning, and historic time-travel simulation.">
        <OrgModulePlaceholder intent="Select an organization to audit configuration vulnerabilities." />
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout
      title="Vulnerability Assessments"
      description="Real-time device exposure audits linked to deep CVE signatures. Slide backward to inspect historic assessments."
      topSlot={
        <div className="cve-dashboard-top">
          {/* Injecting scoped custom styles */}
          <style dangerouslySetInnerHTML={{ __html: `
            .cve-dashboard-top {
              display: flex;
              flex-direction: column;
              gap: 1.5rem;
              margin-bottom: 0.5rem;
              width: 100%;
            }

            /* Metric Cards Grid */
            .cve-metrics-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
              gap: 1.25rem;
              width: 100%;
            }

            .cve-metric-card {
              background: var(--surface-panel);
              border: 1px solid var(--border-subtle);
              border-radius: 12px;
              padding: 1.25rem;
              display: flex;
              flex-direction: column;
              position: relative;
              overflow: hidden;
              box-shadow: 0 4px 12px rgba(var(--brand-ink-rgb), 0.02);
              transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
            }

            .cve-metric-card:hover {
              transform: translateY(-3px);
              box-shadow: 0 10px 22px rgba(var(--brand-ink-rgb), 0.08);
            }

            .cve-metric-card::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              width: 4px;
              height: 100%;
              background: var(--brand-primary);
            }

            .cve-card-severity-critical::before {
              background: linear-gradient(to bottom, hsl(354, 85%, 52%), hsl(340, 85%, 42%));
            }
            .cve-card-severity-high::before {
              background: linear-gradient(to bottom, hsl(25, 95%, 50%), hsl(15, 95%, 40%));
            }
            .cve-card-status-safe::before {
              background: linear-gradient(to bottom, hsl(142, 60%, 45%), hsl(135, 60%, 35%));
            }
            .cve-card-ratio::before {
              background: var(--surface-accent);
            }

            .cve-metric-label {
              font-size: 0.75rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              color: var(--text-secondary);
              margin-bottom: 0.5rem;
            }

            .cve-metric-value-container {
              display: flex;
              align-items: baseline;
              gap: 0.5rem;
            }

            .cve-metric-value {
              font-size: 2rem;
              font-weight: 700;
              color: var(--text-primary);
              line-height: 1;
            }

            .cve-metric-value.color-critical {
              background: linear-gradient(135deg, hsl(354, 85%, 52%), hsl(340, 85%, 42%));
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            .cve-metric-value.color-high {
              background: linear-gradient(135deg, hsl(25, 95%, 52%), hsl(15, 95%, 42%));
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            .cve-metric-value.color-safe {
              background: linear-gradient(135deg, hsl(142, 65%, 40%), hsl(135, 65%, 32%));
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }

            .cve-metric-sub {
              font-size: 0.8rem;
              color: var(--text-secondary);
              margin-top: 0.25rem;
            }

            .cve-metric-ratio-bar {
              height: 4px;
              width: 100%;
              background: var(--brand-gray-100);
              border-radius: 2px;
              margin-top: 0.75rem;
              overflow: hidden;
            }

            .cve-metric-ratio-fill {
              height: 100%;
              background: var(--surface-accent);
              border-radius: 2px;
              transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            }

            /* Time-Travel Controller */
            .cve-time-travel {
              background: var(--surface-panel);
              border: 1px solid var(--border-subtle);
              border-radius: 12px;
              padding: 1.25rem;
              display: flex;
              flex-direction: column;
              gap: 1rem;
              box-shadow: 0 4px 12px rgba(var(--brand-ink-rgb), 0.02);
            }

            .cve-timeline-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-wrap: wrap;
              gap: 0.75rem;
              border-bottom: 1px dashed var(--border-subtle);
              padding-bottom: 0.75rem;
            }

            .cve-timeline-title {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              font-size: 0.95rem;
              font-weight: 700;
              color: var(--text-primary);
            }

            .cve-timeline-status {
              display: inline-flex;
              align-items: center;
              gap: 0.5rem;
              padding: 0.35rem 0.75rem;
              border-radius: 30px;
              font-size: 0.75rem;
              font-weight: 700;
              letter-spacing: 0.02em;
            }

            .cve-status-live {
              background: rgba(16, 185, 129, 0.12);
              color: hsl(142, 70%, 35%);
              border: 1px solid rgba(16, 185, 129, 0.2);
            }

            .cve-status-live .pulsate {
              width: 8px;
              height: 8px;
              background-color: hsl(142, 75%, 45%);
              border-radius: 50%;
              animation: cve-pulse 1.8s infinite;
            }

            .cve-status-traveling {
              background: rgba(245, 158, 11, 0.12);
              color: hsl(25, 85%, 45%);
              border: 1px solid rgba(245, 158, 11, 0.2);
            }

            .cve-status-traveling .badge-icon {
              width: 8px;
              height: 8px;
              background-color: hsl(25, 90%, 45%);
              border-radius: 50%;
            }

            @keyframes cve-pulse {
              0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
              70% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
              100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
            }

            .cve-timeline-inputs {
              display: flex;
              align-items: center;
              gap: 1.5rem;
              flex-wrap: wrap;
            }

            .cve-slider-group {
              flex: 1;
              min-width: 280px;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }

            .cve-slider {
              -webkit-appearance: none;
              width: 100%;
              height: 6px;
              border-radius: 3px;
              background: var(--brand-gray-100);
              outline: none;
              margin: 0.5rem 0;
            }

            .cve-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: var(--brand-primary);
              cursor: pointer;
              border: 2px solid var(--surface-panel);
              box-shadow: 0 2px 6px rgba(var(--brand-ink-rgb), 0.2);
              transition: transform 0.1s ease, background-color 0.2s ease;
            }

            .cve-slider::-webkit-slider-thumb:hover {
              transform: scale(1.15);
              background: var(--brand-primary-strong);
            }

            .cve-slider-ticks {
              display: flex;
              justify-content: space-between;
              font-size: 0.7rem;
              color: var(--text-secondary);
              font-weight: 600;
              padding: 0 0.25rem;
            }

            .cve-slider-tick {
              cursor: pointer;
              transition: color 0.2s ease;
            }

            .cve-slider-tick.is-active {
              color: var(--brand-primary);
              font-weight: 700;
            }

            .cve-custom-picker-group {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              border-left: 1px solid var(--border-subtle);
              padding-left: 1.5rem;
            }

            @media (max-width: 768px) {
              .cve-custom-picker-group {
                border-left: none;
                padding-left: 0;
                width: 100%;
              }
            }

            .cve-custom-picker-group span {
              font-size: 0.75rem;
              font-weight: 600;
              color: var(--text-secondary);
              white-space: nowrap;
            }

            .cve-datetime-input {
              padding: 0.4rem 0.75rem;
              border-radius: 6px;
              border: 1px solid var(--border-subtle);
              background: var(--surface-panel-muted);
              color: var(--text-primary);
              font-size: 0.8rem;
              font-family: var(--font-sans);
            }

            .cve-datetime-input:focus {
              border-color: var(--brand-focus-border);
              outline: none;
              box-shadow: 0 0 0 3px var(--brand-focus-ring);
            }

            .cve-btn-clear-custom {
              padding: 0.4rem 0.75rem;
              border-radius: 6px;
              border: 1px solid var(--border-subtle);
              background: var(--surface-panel);
              color: var(--text-secondary);
              font-size: 0.75rem;
              cursor: pointer;
              font-weight: 600;
              transition: background 0.15s ease;
            }

            .cve-btn-clear-custom:hover {
              background: var(--brand-gray-10);
              color: var(--text-primary);
            }

            /* Custom components styling for grid list */
            .cve-badge-severity {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 0.25rem;
              font-weight: 700;
              font-size: 0.7rem;
              letter-spacing: 0.02em;
              padding: 0.25rem 0.6rem;
              border-radius: 4px;
              line-height: 1;
            }

            .sev-critical {
              background-color: rgba(239, 68, 68, 0.1);
              color: hsl(354, 85%, 48%);
              border: 1px solid rgba(239, 68, 68, 0.15);
            }

            .sev-high {
              background-color: rgba(245, 158, 11, 0.1);
              color: hsl(25, 90%, 45%);
              border: 1px solid rgba(245, 158, 11, 0.15);
            }

            .sev-medium {
              background-color: rgba(59, 130, 246, 0.1);
              color: hsl(210, 85%, 45%);
              border: 1px solid rgba(59, 130, 246, 0.15);
            }

            .sev-low {
              background-color: rgba(107, 114, 128, 0.1);
              color: var(--brand-gray-600);
              border: 1px solid rgba(107, 114, 128, 0.15);
            }

            .cve-cvss-score {
              font-weight: 600;
              opacity: 0.85;
            }

            .cve-badge-status {
              display: inline-block;
              font-weight: 800;
              font-size: 0.68rem;
              letter-spacing: 0.04em;
              padding: 0.25rem 0.5rem;
              border-radius: 4px;
              line-height: 1;
            }

            .status-exposed {
              background-color: rgba(239, 68, 68, 0.15);
              color: hsl(354, 85%, 45%);
              border: 1px solid rgba(239, 68, 68, 0.2);
            }

            .status-safe {
              background-color: rgba(16, 185, 129, 0.12);
              color: hsl(142, 70%, 35%);
              border: 1px solid rgba(16, 185, 129, 0.18);
            }

            .cve-pill-id {
              background: var(--surface-panel-muted);
              border: 1px solid var(--border-subtle);
              border-radius: 6px;
              padding: 0.2rem 0.5rem;
              font-family: var(--font-mono);
              font-size: 0.8rem;
              color: var(--text-primary);
            }

            .cve-cell-device {
              display: flex;
              flex-direction: column;
              gap: 0.15rem;
            }

            .cve-device-name {
              color: var(--text-primary);
              font-size: 0.85rem;
            }

            .cve-device-sub {
              font-size: 0.72rem;
              color: var(--text-secondary);
            }

            .cve-remediation-type {
              font-size: 0.78rem;
              font-weight: 600;
            }

            .cve-remediation-type.type-upgrade {
              color: var(--brand-primary);
            }

            .cve-remediation-type.type-config {
              color: hsl(25, 90%, 45%);
            }

            .cve-remediation-none {
              color: var(--brand-gray-400);
            }

            .cve-timestamp {
              font-size: 0.78rem;
              color: var(--text-secondary);
            }

            /* Filters */
            .cve-filter-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
              gap: 1rem;
              width: 100%;
            }

            .cve-filter-field {
              display: flex;
              flex-direction: column;
              gap: 0.35rem;
              font-size: 0.75rem;
              font-weight: 600;
              color: var(--text-secondary);
              text-transform: uppercase;
              letter-spacing: 0.02em;
            }

            .cve-filter-field input,
            .cve-filter-field select {
              padding: 0.5rem 0.75rem;
              border-radius: 6px;
              border: 1px solid var(--border-subtle);
              background: var(--surface-panel);
              color: var(--text-primary);
              font-size: 0.82rem;
              font-family: var(--font-sans);
              width: 100%;
            }

            .cve-filter-field input:focus,
            .cve-filter-field select:focus {
              border-color: var(--brand-focus-border);
              outline: none;
              box-shadow: 0 0 0 3px var(--brand-focus-ring);
            }

            /* Drawer/Details Styles */
            .cve-drawer-details {
              display: flex;
              flex-direction: column;
              gap: 1.5rem;
              padding: 0.5rem 0;
            }

            .cve-drawer-header-meta {
              display: flex;
              align-items: center;
              gap: 1rem;
              flex-wrap: wrap;
              background: var(--surface-panel-muted);
              border: 1px solid var(--border-subtle);
              border-radius: 8px;
              padding: 0.75rem 1rem;
            }

            .cve-meta-item {
              display: flex;
              flex-direction: column;
              gap: 0.2rem;
            }

            .cve-meta-item span {
              font-size: 0.7rem;
              font-weight: 600;
              color: var(--text-secondary);
              text-transform: uppercase;
            }

            .cve-meta-item strong {
              font-size: 0.9rem;
              color: var(--text-primary);
            }

            .cve-drawer-section {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }

            .cve-section-title {
              font-size: 0.8rem;
              font-weight: 700;
              text-transform: uppercase;
              color: var(--text-secondary);
              letter-spacing: 0.04em;
              border-bottom: 1px solid var(--border-subtle);
              padding-bottom: 0.35rem;
            }

            .cve-justification-text {
              font-size: 0.88rem;
              line-height: 1.5;
              color: var(--text-primary);
              background: var(--brand-meridian-10);
              border: 1px solid var(--brand-meridian-100);
              border-radius: 6px;
              padding: 0.75rem;
            }

            /* Config Console Block */
            .cve-console {
              background: var(--terminal-bg);
              border: 1px solid var(--terminal-border);
              border-radius: 8px;
              padding: 0.75rem;
              font-family: var(--font-mono), monospace;
              color: var(--terminal-fg);
              font-size: 0.8rem;
              line-height: 1.4;
              max-height: 250px;
              overflow-y: auto;
              white-space: pre-wrap;
              word-break: break-all;
            }

            .cve-console-line {
              display: flex;
              gap: 0.75rem;
            }

            .cve-console-num {
              color: var(--text-secondary);
              text-align: right;
              width: 24px;
              user-select: none;
              opacity: 0.5;
            }

            .cve-console-code {
              color: var(--terminal-fg);
              flex: 1;
            }

            .cve-console-match {
              background: var(--terminal-highlight);
              font-weight: 600;
              border-left: 2px solid var(--brand-accent);
              padding-left: 2px;
            }

            /* Commands Box */
            .cve-command-box {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }

            .cve-command-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              background: var(--brand-gray-10);
              border: 1px solid var(--border-subtle);
              border-radius: 6px;
              padding: 0.5rem 0.75rem;
              gap: 0.75rem;
            }

            .cve-command-text {
              font-family: var(--font-mono), monospace;
              font-size: 0.8rem;
              color: var(--text-primary);
              word-break: break-all;
              flex: 1;
            }

            .cve-btn-copy {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              border: 1px solid var(--border-subtle);
              background: var(--surface-panel);
              border-radius: 4px;
              padding: 0.25rem 0.5rem;
              font-size: 0.7rem;
              font-weight: 700;
              color: var(--text-secondary);
              cursor: pointer;
              transition: all 0.15s ease;
            }

            .cve-btn-copy:hover {
              background: var(--brand-primary-soft);
              color: var(--brand-primary);
              border-color: var(--brand-primary);
            }

            .cve-btn-copy.copied {
              background: rgba(16, 185, 129, 0.1);
              color: hsl(142, 70%, 35%);
              border-color: hsl(142, 70%, 35%);
            }

            .cve-impact-box {
              font-size: 0.85rem;
              line-height: 1.45;
              color: hsl(25, 90%, 40%);
              background: rgba(245, 158, 11, 0.08);
              border: 1px solid rgba(245, 158, 11, 0.18);
              border-radius: 6px;
              padding: 0.75rem;
              font-style: italic;
            }

            .cve-impact-box-container {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              margin-top: 0.25rem;
            }

            .cve-plane-badge {
              align-self: flex-start;
              font-size: 0.72rem;
              font-weight: 700;
              letter-spacing: 0.04em;
              text-transform: uppercase;
              padding: 0.2rem 0.5rem;
              border-radius: 4px;
              display: inline-flex;
              align-items: center;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            }

            .cve-plane-management {
              background: hsla(210, 100%, 50%, 0.1);
              color: hsl(210, 100%, 65%);
              border: 1px solid hsla(210, 100%, 50%, 0.3);
            }

            .cve-plane-control {
              background: hsla(280, 100%, 50%, 0.1);
              color: hsl(280, 100%, 65%);
              border: 1px solid hsla(280, 100%, 50%, 0.3);
            }

            .cve-plane-data {
              background: hsla(340, 100%, 50%, 0.1);
              color: hsl(340, 100%, 65%);
              border: 1px solid hsla(340, 100%, 50%, 0.3);
            }

            .cve-remediation-box-upgrade {
              background: var(--brand-meridian-10);
              border: 1px solid var(--brand-meridian-100);
              border-radius: 6px;
              padding: 0.75rem;
              font-size: 0.88rem;
              line-height: 1.5;
              color: var(--text-primary);
            }
          ` }} />

          {/* Metric KPI Cards */}
          <div className="cve-metrics-grid">
            <div className="cve-metric-card cve-card-severity-critical">
              <span className="cve-metric-label">Active Exposure Ratio</span>
              <div className="cve-metric-value-container">
                <span className="cve-metric-value color-critical">{displayRatio}%</span>
              </div>
              <div className="cve-metric-ratio-bar">
                <div className="cve-metric-ratio-fill" style={{ width: `${displayRatio}%` }} />
              </div>
              <span className="cve-metric-sub">
                {summaryData ? summaryData.activeExposures : 0} of {summaryData ? (summaryData.activeExposures + summaryData.auditedSafe) : 0} assessments are active
              </span>
            </div>

            <div className="cve-metric-card cve-card-severity-high">
              <span className="cve-metric-label">Active Exposures</span>
              <div className="cve-metric-value-container">
                <span className="cve-metric-value color-high">{summaryData ? summaryData.activeExposures : 0}</span>
              </div>
              <span className="cve-metric-sub">
                Crit: {summaryData ? summaryData.criticalExposures : 0} | High: {summaryData ? summaryData.highExposures : 0} | Med: {summaryData ? summaryData.mediumExposures : 0}
              </span>
            </div>

            <div className="cve-metric-card cve-card-status-safe">
              <span className="cve-metric-label">Audited Safe</span>
              <div className="cve-metric-value-container">
                <span className="cve-metric-value color-safe">{summaryData ? summaryData.auditedSafe : 0}</span>
              </div>
              <span className="cve-metric-sub">Devices tested with conforming configurations</span>
            </div>

            <div className="cve-metric-card cve-card-ratio">
              <span className="cve-metric-label">Total Signatures</span>
              <div className="cve-metric-value-container">
                <span className="cve-metric-value">{summaryData ? summaryData.totalCves : 0}</span>
              </div>
              <span className="cve-metric-sub">Unique matches registered in cve-db database</span>
            </div>
          </div>

          {/* Timeline Time-Travel controller */}
          <div className="cve-time-travel">
            <div className="cve-timeline-header">
              <div className="cve-timeline-title">
                <ShieldIcon size={18} />
                <span>Historic Audit Timeline Simulation (Time-Travel)</span>
              </div>
              <div className={`cve-timeline-status ${resolvedTimestamp ? 'cve-status-traveling' : 'cve-status-live'}`}>
                {resolvedTimestamp ? (
                  <>
                    <span className="badge-icon" />
                    <span>TIME-TRAVEL ACTIVE (HISTORICAL VIEW)</span>
                  </>
                ) : (
                  <>
                    <span className="pulsate" />
                    <span>REAL-TIME AUDIT ACTIVE</span>
                  </>
                )}
              </div>
            </div>

            <div className="cve-timeline-inputs">
              <div className="cve-slider-group">
                <input
                  type="range"
                  min="0"
                  max={PRESET_TIMELINE.length - 1}
                  step="1"
                  value={sliderIndex}
                  onChange={handleSliderChange}
                  className="cve-slider"
                />
                <div className="cve-slider-ticks">
                  {PRESET_TIMELINE.map((preset, index) => (
                    <span
                      key={preset.label}
                      className={`cve-slider-tick ${sliderIndex === index && !customTimestamp ? 'is-active' : ''}`}
                      onClick={() => {
                        setCustomTimestamp('');
                        setSliderIndex(index);
                      }}
                    >
                      {preset.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="cve-custom-picker-group">
                <span>Or exact datetime:</span>
                <input
                  type="datetime-local"
                  value={customTimestamp}
                  onChange={handleCustomTimestampChange}
                  className="cve-datetime-input"
                />
                {customTimestamp && (
                  <button type="button" className="cve-btn-clear-custom" onClick={handleClearCustomTimestamp}>
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      }
    >
      {/* Table grid area */}
      <ListWorkspace
        rows={pagedExposures}
        columns={columns}
        rowId={(row, index) => row.id || `${row.deviceId}-${row.cveId}`}
        totalCount={assessmentsData?.meta?.exactTotal ?? assessmentsData?.items?.length ?? 0}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100, 250, 500, 1000]}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        loading={loading}
        emptyState={loading ? <p>Analyzing running configurations against CVE db…</p> : <p>No matching vulnerability assessments found.</p>}
        headerLabel="Vulnerability Assessments"
        filterBar={filterBarContent}
        filterDefaultOpen={true}
        filterActive={
          searchQuery.trim().length > 0 ||
          severityFilter !== 'ALL' ||
          statusFilter !== 'ALL' ||
          selectedSiteId !== 'ALL' ||
          selectedDeviceId !== 'ALL'
        }
        onFilterReset={() => {
          setSearchQuery('');
          setSeverityFilter('ALL');
          setStatusFilter('ALL');
          setSelectedSiteId('ALL');
          setSelectedDeviceId('ALL');
        }}
        onRowClick={handleRowClick}
        highlightRowId={selectedExposure ? (selectedExposure.id || `${selectedExposure.deviceId}-${selectedExposure.cveId}`) : null}
        selectionDisabled
        onRefresh={handleRefresh}
        headerStatus={
          apiError ? (
            <div className="list-workspace__status" style={{ color: 'hsl(354, 85%, 45%)', fontWeight: 'bold' }}>
              Failed to sync vulnerability assessments: {apiError.message}
            </div>
          ) : resolvedTimestamp ? (
            <div className="list-workspace__status" style={{ color: 'hsl(25, 90%, 40%)', fontStyle: 'italic' }}>
              Historical Audit computed on-the-fly for {formatDateTime(resolvedTimestamp)} (Time-Travel).
            </div>
          ) : undefined
        }
      />

      {/* Tactile Sliding Detail Drawer */}
      <Drawer
        open={selectedExposure != null}
        title={`Assessment: ${selectedExposure?.cveId || ''}`}
        subtitle={`${selectedExposure?.deviceName || 'Device'} (${selectedExposure?.siteName || 'Site'})`}
        onClose={() => setSelectedExposure(null)}
        width="wide"
      >
        {selectedExposure ? (
          <div className="cve-drawer-details">
            <div className="cve-drawer-header-meta">
              <div className="cve-meta-item">
                <span>Vulnerability</span>
                <strong>{selectedExposure.cveId}</strong>
              </div>
              <div className="cve-meta-item">
                <span>Severity</span>
                <span className={`cve-badge-severity sev-${selectedExposure.severity.toLowerCase()}`}>
                  {selectedExposure.severity}
                  {selectedExposure.cvssScore !== undefined && ` (${selectedExposure.cvssScore.toFixed(1)})`}
                </span>
              </div>
              <div className="cve-meta-item">
                <span>Status</span>
                <span className={`cve-badge-status ${selectedExposure.status === 'EXPOSED' ? 'status-exposed' : 'status-safe'}`}>
                  {selectedExposure.status === 'EXPOSED' ? 'EXPOSED' : 'AUDITED SAFE'}
                </span>
              </div>
              <div className="cve-meta-item">
                <span>Assessed Date</span>
                <strong>{formatDateTime(selectedExposure.assessedAt)}</strong>
              </div>
            </div>

            <div className="cve-drawer-section">
              <span className="cve-section-title">Evaluation Justification</span>
              <div className="cve-justification-text">{selectedExposure.justification}</div>
            </div>

            {selectedExposure.causativeConfig && (
              <div className="cve-drawer-section">
                <span className="cve-section-title">
                  {selectedExposure.status === 'EXPOSED'
                    ? 'Causative Running Configuration Lines'
                    : 'Configuration Audit Evidence (Required Patterns Absent/Negated)'}
                </span>
                <div className="cve-console">
                  {selectedExposure.causativeConfig.split('\n').map((line, idx) => (
                    <div key={idx} className="cve-console-line">
                      <span className="cve-console-num">{idx + 1}</span>
                      <span className="cve-console-code cve-console-match">{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedExposure.status === 'EXPOSED' && (
              <div className="cve-drawer-section">
                <span className="cve-section-title">Remediation Action</span>
                <div className="cve-remediation-box-upgrade" style={{ marginBottom: selectedExposure.remediationCommands && selectedExposure.remediationCommands.length > 0 ? '1rem' : '0' }}>
                  🔀 <strong>Primary Recommendation: Firmware / Software Upgrade</strong>
                  <br />
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    To completely resolve this vulnerability, upgrade the device to the recommended secure software image release.
                  </span>
                  <div style={{ marginTop: '0.5rem' }}>
                    Target patch release: <strong>{selectedExposure.patchUpgrade || 'Latest secure release'}</strong>
                  </div>
                </div>

                {selectedExposure.remediationCommands && selectedExposure.remediationCommands.length > 0 && (
                  <div className="cve-command-box">
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem 0' }}>
                      🔧 <strong>Configuration Workaround / Mitigation (Non-disruptive)</strong>
                    </p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem 0' }}>
                      If an immediate software upgrade is not feasible, apply the following CLI commands to mitigate the active exposure:
                    </p>
                    {selectedExposure.remediationCommands.map((cmd, idx) => (
                      <div key={idx} className="cve-command-row">
                        <span className="cve-command-text">{cmd}</span>
                        <button
                          type="button"
                          className={`cve-btn-copy ${copiedIndex === idx ? 'copied' : ''}`}
                          onClick={() => handleCopyCommand(cmd, idx)}
                        >
                          {copiedIndex === idx ? 'Copied! ✓' : 'Copy'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedExposure.status === 'EXPOSED' && selectedExposure.impactOfFix && (() => {
              const rawImpact = selectedExposure.impactOfFix;
              let planeType = '';
              let impactText = rawImpact;
              
              if (rawImpact.startsWith('Management Plane Impact:')) {
                planeType = 'Management Plane';
                impactText = rawImpact.substring('Management Plane Impact:'.length).trim();
              } else if (rawImpact.startsWith('Control Plane Impact:')) {
                planeType = 'Control Plane';
                impactText = rawImpact.substring('Control Plane Impact:'.length).trim();
              } else if (rawImpact.startsWith('Data Plane Impact:')) {
                planeType = 'Data Plane';
                impactText = rawImpact.substring('Data Plane Impact:'.length).trim();
              }
              
              return (
                <div className="cve-drawer-section">
                  <span className="cve-section-title">Operational Impact Statement</span>
                  <div className="cve-impact-box-container">
                    {planeType && (
                      <span className={`cve-plane-badge cve-plane-${planeType.split(' ')[0].toLowerCase()}`}>
                        {planeType}
                      </span>
                    )}
                    <div className="cve-impact-box">
                      ⚠️ {impactText}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : null}
      </Drawer>
    </OrgModuleLayout>
  );
};

export default CVEGlobalSummaryPage;
